import { makeAutoObservable } from "mobx";
import { RArray, RMutableMap } from "../../collections";
import { IdentifiableMap } from "../../collections/IdentifiableMap";
import { RMutableMultiSet } from "../../collections/RMutableMultiSet";
import { randomUUID } from "../../id";
import { Availability } from "../Availability";
import { ModifierItemInstance, ModifierItemInstanceId, } from "../ModifierItemInstance";
import { fromModifierItems } from "../PackingItem";
import { ParameterScope } from "../ParameterScope";
import { PositiveQuantity } from "../PositiveQuantity";
import { Price } from "../Price";
import { Quantity } from "../Quantity";
import { ModifierInstanceEditability } from "./ModifierInstanceEditability";
import { ModifierInstanceId } from "./ModifierInstanceId";
export class ModifierInstance {
    constructor(params) {
        this.instanceId = params.instanceId;
        this.scope = ParameterScope.Uninitialized;
        this.behaviour = params.behaviour;
        if (params.modifierType.behaviour !== this.behaviour) {
            throw new Error("Modifier behaviour mismatch, please use instantiate method to obtain the correct instance");
        }
        this.modifierType = params.modifierType;
        this.itemQuantities = params.itemQuantities;
        this.items = params.items;
        this.freeItems = params.freeItems;
        this.editability = params.editability;
        makeAutoObservable(this);
    }
    setScope(scope) {
        this.scope = scope;
        this.editability.setScope(this.scope);
        this.items.objects.forEach((modifierItemInstance) => {
            modifierItemInstance.setScope(this.scope);
        });
    }
    get typeId() {
        return this.modifierType.id;
    }
    /** Item entries, only items which are selected. */
    get selectedItems() {
        return this.items.filter((itemInstance) => itemInstance.isSelected).objects;
    }
    get nonFreeSelectedItemEntries() {
        const sorted = this.selectedItems.sorted((a, b) => -1 * a.unitPrice.compare(b.unitPrice));
        let freeItems = this.freeItems;
        return sorted.map((itemInstance) => {
            if (itemInstance.isDefault) {
                return { itemInstance, nonFreeQuantity: itemInstance.quantity };
            }
            if (freeItems >= itemInstance.quantity.value) {
                freeItems = freeItems - itemInstance.quantity.value;
                return { itemInstance, nonFreeQuantity: Quantity.Zero };
            }
            const newQuanitity = itemInstance.quantity.value - freeItems;
            freeItems = 0;
            return {
                itemInstance,
                nonFreeQuantity: Quantity.fromNumber(newQuanitity),
            };
        });
    }
    get modifierTypeAvailability() {
        return Availability.ofModifierType(this.modifierType, this.scope);
    }
    get availability() {
        const selectedItemCount = this.selectedItems.reduce((acc, itemInstance) => acc.add(itemInstance.quantity), Quantity.Zero).value;
        const availabilities = new RArray(this.selectedItems.map((itemInstance) => itemInstance.unitAvailability));
        const itemAvailabilities = Availability.dependent("ModifierItem", "", Availability.composite(availabilities.raw));
        return Availability.composite([
            this.modifierTypeAvailability,
            itemAvailabilities,
            Availability.boolean({
                NotExpectedItems: this.modifierType.minItems === this.modifierType.maxItems &&
                    selectedItemCount !== this.modifierType.minItems,
                MaxItemsExceeded: this.modifierType.maxItems !== null &&
                    selectedItemCount > this.modifierType.maxItems,
                NotEnoughItems: selectedItemCount < this.modifierType.minItems,
            }),
        ]);
    }
    /**
     * Should this modifier instance be taken into ProductConfig and displayed inside creator?
     * Rules implemented here are related to the feature of hiding unavailable modifiers
     */
    /**
     * The idea of hiding unavailable modifiers makes lot of troubles in code.
     * It fiters out modifiers in Cart and should work only in creator.
     * Eg. Default value in fulfillment method is DineIn so during refreshing page we use ProductInstanceCreator
     * which filters out modifiers which are not available in DineIn fulfilment method.
     **/
    get isFinal() {
        return this.modifierTypeAvailability.isAvailable.isPossible;
    }
    /** See {@link Creator.fixAvailability} */
    fixAvailability() {
        for (const itemInstance of this.selectedItems) {
            itemInstance.fixAvailability();
        }
    }
    get packingItems() {
        const quantities = this.selectedItems.map((itemInstance) => [
            itemInstance.typeId,
            new PositiveQuantity(itemInstance.quantity.value),
        ]);
        return fromModifierItems(this.modifierType, quantities);
    }
    /**
     * Price of non free selected items
     */
    get selectionPrice() {
        return Price.total(this.nonFreeSelectedItemEntries.map(({ itemInstance, nonFreeQuantity }) => itemInstance.unitPrice.multiply(nonFreeQuantity)));
    }
    /**
     * Price of items selected by default
     *
     * TODO: what if one of the items here has no price?
     */
    get defaultSelectionPrice() {
        return Price.total(this.selectedItems.map((itemInstance) => itemInstance.defaultPrice));
    }
    /**
     * Price of an union of selected and default items
     */
    get onlyAddedSelectionPrice() {
        return Price.total(this.nonFreeSelectedItemEntries.map(({ itemInstance, nonFreeQuantity }) => {
            const onlyAddedQuantity = nonFreeQuantity.sub(itemInstance.defaultQuantity);
            return itemInstance.unitPrice.multiply(onlyAddedQuantity);
        }));
    }
    get price() {
        return this.modifierType.exchangePolicy.price({
            selectionPrice: this.selectionPrice,
            defaultSelectionPrice: this.defaultSelectionPrice,
            addedOnlyPrice: this.onlyAddedSelectionPrice,
        });
    }
    get selectedItemIds() {
        return this.items.objects.flatMap(({ typeId, quantity }) => RArray.repeat(typeId, quantity.value));
    }
    set selectedItemIds(selectedItemTypeIds) {
        var _a;
        const counts = RMutableMap.empty();
        for (const itemId of selectedItemTypeIds) {
            const count = counts.getOrCreate(itemId, () => Quantity.Zero);
            counts.update(itemId, count.incremented());
        }
        for (const itemInstance of this.items.objects) {
            const count = (_a = counts.find(itemInstance.typeId)) !== null && _a !== void 0 ? _a : Quantity.Zero;
            this.itemQuantities.setQuantity(itemInstance.typeId, count);
        }
    }
    clone() {
        const itemInstances = this.items.objects;
        const itemQuantities = RMutableMultiSet.fromIterable(itemInstances.map((itemInstance) => [
            itemInstance.typeId,
            itemInstance.defaultQuantity,
        ]));
        const modifierInstance = new ModifierInstance({
            instanceId: new ModifierInstanceId(randomUUID()),
            behaviour: this.behaviour,
            modifierType: this.modifierType,
            itemQuantities,
            items: IdentifiableMap.fromIterable("typeId", itemInstances.map((itemInstance) => itemInstance.clone(itemQuantities))),
            freeItems: this.freeItems,
            editability: this.editability.clone(),
        });
        modifierInstance.selectedItemIds = this.selectedItemIds;
        return modifierInstance;
    }
    selectInferredDefaults() {
        if (this.selectedItems.isEmpty && this.modifierType.minItems === 1) {
            const availableItems = this.items.filter((itemInstance) => itemInstance.unitAvailability.isAvailable.isPossible);
            if (availableItems.size === 1) {
                availableItems.sample.select();
            }
        }
    }
    static create(modifierType, settings) {
        const instanceId = new ModifierInstanceId(randomUUID());
        const isMultipleChoice = modifierType.behaviour === "MultipleChoice";
        const editability = new ModifierInstanceEditability({
            editabilityParametrization: modifierType.editabilityParametrization,
        });
        const itemQuantities = RMutableMultiSet.fromIterable(settings.defaultItems);
        const items = IdentifiableMap.fromIterable("typeId", modifierType.itemTypes.objects.map((modifierItemType) => {
            var _a;
            return new ModifierItemInstance({
                itemQuantities,
                instanceId: new ModifierItemInstanceId(randomUUID()),
                modifierItemType,
                defaultQuantity: (_a = settings.defaultItems.find(modifierItemType.id)) !== null && _a !== void 0 ? _a : Quantity.Zero,
                maxDuplicatesPerItem: modifierType.maxDuplicatesPerItem,
                isMultipleChoice,
                editability,
            });
        }));
        return new ModifierInstance({
            instanceId,
            behaviour: modifierType.behaviour,
            modifierType,
            itemQuantities,
            items,
            freeItems: settings.freeItems,
            editability,
        });
    }
}
